import { ConnectedCrmLogos } from "@/components/integrations/connected-crm-logos";
import { IntegrationDropdownContent } from "@/components/integrations/employee/integration-dropdown-content";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useCrmRecordPresence } from "@/hooks/useCrmRecordPresence";
import {
  ConnectedCrmIntegration,
  useConnectedCrmIntegrations,
} from "@/hooks/useConnectedCrmIntegrations";
import { useEmployeeEmailReveal } from "@/hooks/useEmployeeEmailReveal";
import { useSeliarisOwnerChoice } from "@/hooks/useSeliarisOwnerChoice";
import { useTranslation } from "@/hooks/useTranslation";
import { canRevealEmployeeEmail } from "@/lib/employee-email-reveal";
import {
  getSeliarisErrorFields,
  getSeliarisErrorMessageKey,
} from "@/lib/seliaris-errors";
import { cn } from "@/lib/utils";
import {
  ContactCrmIntegration,
  CrmIntegrationProviderSupported,
  CrmSendOptions,
  EmployeeDTO,
  SyncStatusCrmIntegrationDTO,
} from "@shaarp/common";
import { ExternalLink } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { MdInsertLink } from "react-icons/md";

export type SyncContact = {
  id: string;
  directLink: string;
};

/**
 * One connected CRM and what this contact's state is in it. The record and the
 * candidates live per integration: with two CRMs connected, neither is
 * meaningful without the provider it came from.
 */
export type ContactIntegrationStatus = ConnectedCrmIntegration & {
  isSynced: boolean;
  isLoading: boolean;
  /**
   * The candidate currently being linked, when the send targets one — only that
   * row spins, the other candidates of the same CRM just wait.
   */
  loadingMatchId: string | null;
  syncEndpoint: string;
  crmRecord: SyncContact | null;
  possibleMatches: ContactCrmIntegration[];
};

/** What the CRM was told about a contact whose company Shaarp never resolved. */
type SyncPayload = CrmSendOptions & {
  externalContactId?: string;
  companyName?: string;
};

export default function EmployeeIntegrationsSync({
  employee,
  companyId,
  onSynced,
  showLabel = false,
}: {
  employee: EmployeeDTO;
  companyId?: string;
  onSynced?: (update: Partial<EmployeeDTO> & { id: string }) => void;
  showLabel?: boolean;
}) {
  const api = useApi();
  const { toast } = useToast();
  const toastError = useToastError();
  const { t } = useTranslation();
  const { revealEmail } = useEmployeeEmailReveal();
  const { sendWithOwnerChoice, ownerDialog } = useSeliarisOwnerChoice();

  const connectedIntegrations = useConnectedCrmIntegrations();

  // Contacts whose company is not resolved in our database (e.g. search
  // results) are synced through the company-less endpoint; the company name is
  // sent in the request body so it is kept on the CRM contact.
  const buildEndpoint = (provider: CrmIntegrationProviderSupported) =>
    companyId
      ? `/company/${companyId}/employee/${employee.id}/integration/crm/${provider}`
      : `/company/employee/${employee.id}/integration/crm/${provider}`;

  const [open, setOpen] = useState(false);
  const [integrations, setIntegrations] = useState<ContactIntegrationStatus[]>(
    [],
  );

  const [isSynced, setIsSynced] = useState(false);
  const [crmContact, setCrmContact] = useState<SyncContact | null>(null);

  // Rebuilt from the DTO whenever the contact or the set of connected CRMs
  // changes, so a newly connected provider shows up without a page reload.
  useEffect(() => {
    const statuses = connectedIntegrations.map((integration) => {
      const syncStatus = employee.integrations?.[integration.key];
      return {
        ...integration,
        isSynced: !!syncStatus?.isSynced,
        isLoading: false,
        loadingMatchId: null,
        syncEndpoint: buildEndpoint(integration.key),
        crmRecord:
          syncStatus?.externalId && syncStatus?.directLink
            ? { id: syncStatus.externalId, directLink: syncStatus.directLink }
            : null,
        possibleMatches: syncStatus?.possibleMatches ?? [],
      };
    });

    setIntegrations(statuses);

    const firstSynced = statuses.find((status) => status.isSynced);
    if (firstSynced) {
      setIsSynced(true);
      if (firstSynced.crmRecord) setCrmContact(firstSynced.crmRecord);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee.id, employee.integrations, connectedIntegrations, companyId]);

  // What the owner dialog calls the contact: its name, else its address.
  const contactName =
    [employee.firstName, employee.lastName].filter(Boolean).join(" ") ||
    employee.email ||
    "";

  const possiblesMatchCrmContacts = useMemo(
    () => integrations.flatMap((integration) => integration.possibleMatches),
    [integrations],
  );

  /**
   * Marks a single CRM as busy. Each integration carries its own flag: sending
   * a contact to one CRM must leave the row of the other one untouched.
   */
  const setIntegrationLoading = (
    integrationKey: CrmIntegrationProviderSupported,
    isLoading: boolean,
    loadingMatchId: string | null = null,
  ) => {
    setIntegrations((prev) =>
      prev.map((integration) =>
        integration.key === integrationKey
          ? {
              ...integration,
              isLoading,
              loadingMatchId: isLoading ? loadingMatchId : null,
            }
          : integration,
      ),
    );
  };

  /** Records the CRM record this contact now has, locally and for the row. */
  const applySyncResult = (
    integrationKey: CrmIntegrationProviderSupported,
    data: SyncStatusCrmIntegrationDTO<ContactCrmIntegration> | undefined,
  ) => {
    setIsSynced(true);

    const record: SyncContact = {
      id: data?.externalId || "",
      directLink: data?.directLink || "",
    };
    if (data) {
      setCrmContact(record);
      onSynced?.({
        id: employee.id,
        integrations: {
          ...employee.integrations,
          [integrationKey]: {
            isSynced: true,
            externalId: data.externalId ?? null,
            directLink: data.directLink ?? null,
            possibleMatches: [],
          },
        },
      });
    }

    setIntegrations((prev) =>
      prev.map((integration) =>
        integration.key === integrationKey
          ? {
              ...integration,
              isSynced: true,
              crmRecord: data ? record : integration.crmRecord,
              possibleMatches: [],
            }
          : integration,
      ),
    );
  };

  /**
   * Shows the contact as not sent to the CRMs that deleted its record, so the
   * menu offers to send it again, and tells the row it came from.
   */
  const forgetDeletedRecords = (
    deletedIn: readonly CrmIntegrationProviderSupported[],
  ) => {
    const next = integrations.map((integration) =>
      deletedIn.includes(integration.key)
        ? {
            ...integration,
            isSynced: false,
            crmRecord: null,
            possibleMatches: [],
          }
        : integration,
    );
    setIntegrations(next);

    const stillSynced = next.find((integration) => integration.isSynced);
    setIsSynced(!!stillSynced);
    setCrmContact(stillSynced?.crmRecord ?? null);

    const forgotten = { ...employee.integrations };
    for (const key of deletedIn) {
      forgotten[key] = {
        isSynced: false,
        externalId: null,
        directLink: null,
        possibleMatches: [],
      };
    }
    onSynced?.({ id: employee.id, integrations: forgotten });
  };

  const { cancel: cancelRecordCheck } = useCrmRecordPresence({
    path: `/company/employee/${employee.id}/integration/crm`,
    isOpen: open,
    hasLinkedRecord: integrations.some((integration) => integration.isSynced),
    onDeleted: forgetDeletedRecords,
  });

  /**
   * The contact's email, revealed on the spot when the CRM about to receive it
   * refuses a contact without one and Shaarp does not hold it yet.
   *
   * The reveal is the same one the email cell triggers, so the address is
   * persisted and the row it came from is told about it — the user sees the
   * address appear next to the contact they just sent.
   *
   * @returns The address to send; null when the contact has none; or
   *   `"failed"` when the reveal itself failed — it already told the user why,
   *   and saying the contact has no email on top would be false.
   */
  const resolveContactEmail = async (): Promise<string | null | "failed"> => {
    if (employee.email) return employee.email;
    if (!canRevealEmployeeEmail(employee)) return null;

    const revealed = await revealEmail({
      employeeId: employee.id,
      companyId,
    });
    if (!revealed) return "failed";

    onSynced?.({
      id: employee.id,
      email: revealed.email ?? null,
      emailStatus: revealed.emailStatus,
    });

    return revealed.email ?? null;
  };

  const buildSyncPayload = (
    externalContactId: string | undefined,
    withoutOwner: boolean,
  ): SyncPayload => {
    const payload: SyncPayload = {
      ...(externalContactId ? { externalContactId } : {}),
      ...(withoutOwner ? { withoutOwner: true } : {}),
    };

    // No resolved company: pass the name so the CRM contact keeps it.
    if (!companyId) {
      const companyName = employee.companyName ?? employee.domain ?? undefined;
      if (companyName) payload.companyName = companyName;
    }

    return payload;
  };

  const notifySynced = (integrationName: string) => {
    toast({
      variant: "default",
      title: t("employee.integrations.syncTitle", {
        integration: integrationName,
      }),
      description: t("employee.integrations.syncSuccess", {
        integration: integrationName,
      }),
    });
  };

  /**
   * Names what the CRM refused when it said so, and falls back to the generic
   * toast otherwise. A contact Seliaris turns down for a missing email is the
   * common case, and the user can fix it — but only once told.
   */
  const reportSyncError = (error: unknown, integrationName: string) => {
    const messageKey = getSeliarisErrorMessageKey(error);
    if (!messageKey) {
      toastError();
      return;
    }
    toast({
      variant: "destructive",
      title: t("employee.integrations.syncErrorTitle", {
        integration: integrationName,
      }),
      // The custom fields a refusal named are interpolated when it carried
      // any; every other message ignores the parameter.
      description: t(messageKey, { fields: getSeliarisErrorFields(error) }),
    });
  };

  const reportMissingEmail = (integrationName: string) => {
    toast({
      variant: "destructive",
      title: t("employee.integrations.syncErrorTitle", {
        integration: integrationName,
      }),
      description: t("employee.integrations.emailNotFoundError", {
        integration: integrationName,
      }),
    });
  };

  /**
   * Creates or refreshes this contact in the given CRM, or links it to the
   * existing record the user picked.
   *
   * When Seliaris knows no user with the sender's email, the sender is asked
   * whether to send the contact — and the company created with it, which the
   * question names when the contact has one — without an owner; a cancel
   * sends no second request and does not roll back an earlier company
   * creation.
   */
  const handleSyncToIntegration = async (
    integrationKey: CrmIntegrationProviderSupported,
    externalContactId?: string,
  ) => {
    const integration = integrations.find((i) => i.key === integrationKey);
    if (!integration) return;

    cancelRecordCheck();
    setIntegrationLoading(integrationKey, true, externalContactId ?? null);

    try {
      // A CRM that will not take a contact without an email gets one first:
      // the send is pointless otherwise, and its refusal would reach the user
      // as a validation error naming a field they never saw.
      const isCreatingContact = !externalContactId && !integration.isSynced;
      let revealedEmail: string | null = null;
      if (integration.requiresContactEmail && isCreatingContact) {
        const resolved = await resolveContactEmail();
        if (resolved === "failed") return;
        if (!resolved) {
          reportMissingEmail(integration.name);
          return;
        }
        revealedEmail = resolved;
      }

      const response = await sendWithOwnerChoice(
        // Named after the reveal: a contact with no name is called by the
        // address it was just given, not by what it had at click time.
        contactName || revealedEmail || "",
        (withoutOwner) => {
          const payload = buildSyncPayload(externalContactId, withoutOwner);
          return api.post<SyncStatusCrmIntegrationDTO<ContactCrmIntegration>>(
            integration.syncEndpoint,
            Object.keys(payload).length > 0 ? payload : undefined,
          );
        },
        // Only a contact sent with its company can create one in Seliaris.
        { onAsk: () => setOpen(false), mayCreateCompany: !!companyId },
      );
      if (!response) return;
      const { data } = response;

      applySyncResult(integrationKey, data);
      notifySynced(integration.name);
    } catch (error) {
      reportSyncError(error, integration.name);
    } finally {
      // Cleared here rather than per branch: the send also gives up before
      // ever reaching the CRM when the contact has no email to offer it.
      setIntegrationLoading(integrationKey, false);
    }
  };

  /** Opens a CRM record in a new tab, whether it is the synced one or a match. */
  const openCrmRecord = (record: { directLink?: string | null } | null) => {
    if (record?.directLink) window.open(record.directLink, "_blank");
  };

  // The synced record when there is one, the first candidate otherwise — the
  // side button always points at whatever the trigger is advertising.
  const externalRecord =
    isSynced && crmContact ? crmContact : possiblesMatchCrmContacts[0];

  if (connectedIntegrations.length === 0) return null;

  return (
    // Labelled, the pair is a full-width row: the trigger takes what the
    // external-link button leaves, so the group never spills out of its card.
    <div className={cn("flex items-center", showLabel && "w-full")}>
      {ownerDialog}
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size={showLabel ? "sm" : "default"}
            aria-expanded={open}
            className={cn(
              showLabel && "min-w-0 flex-1",
              externalRecord && "pr-2 rounded-r-none border-r-0",
            )}
          >
            {/* Every connected CRM shows its own logo, so the button says which
                ones it talks to rather than repeating the linked badge. */}
            <ConnectedCrmLogos integrations={connectedIntegrations} />
            {showLabel && (
              <span className="truncate">
                {t("employee.integrations.linkToCrm")}
              </span>
            )}
            {isSynced ? (
              <MdInsertLink className="shrink-0 w-4 h-4 text-success" />
            ) : possiblesMatchCrmContacts.length > 0 ? (
              <Badge variant="outline" className="rounded-full">
                {possiblesMatchCrmContacts.length}
              </Badge>
            ) : null}
          </Button>
        </DropdownMenuTrigger>
        <IntegrationDropdownContent
          integrations={integrations}
          onSync={handleSyncToIntegration}
          onOpenExternal={openCrmRecord}
        />
      </DropdownMenu>
      {externalRecord && (
        <ExternalContactItem
          isCompact={showLabel}
          onClick={() => openCrmRecord(externalRecord)}
        />
      )}
    </div>
  );
}

const ExternalContactItem = ({
  isCompact,
  onClick,
}: {
  // Matches the trigger's height, which shrinks once it carries its label.
  isCompact: boolean;
  onClick: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          size={isCompact ? "icon-sm" : "icon"}
          className="rounded-l-none border-l-0 [&_svg]:size-3.5!"
          onClick={onClick}
        >
          <ExternalLink />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{t("employee.integrations.openInCrmTooltip")}</p>
      </TooltipContent>
    </Tooltip>
  );
};
